import EditIcon from '@mui/icons-material/Edit'
import { IconButton, Tooltip } from '@mui/material'
import { MRT_RowData } from 'material-react-table'
import { useCallback, useEffect, useMemo, useState } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'

import { getInitCellValue } from '@/components'
import { CellTooltip } from '@/components'
import { MultilinePreview } from '@/components'
import { AceEditorWithoutForm } from '@/components/AceEditor/AceEditor'
import {
	EditorModalHeader,
	getRowElementLabel,
} from '@/components/EditorModalHeader'
import { ModalForm } from '@/components/Modal/ModalForm'
import { useSelectRowOnOpen } from '@/components/MrtTable/hooks/useSelectRowOnOpen'
import { ScriptModalCellProps } from '@/components/MrtTable/types'
import { useAppContext } from '@/hooks'
import { cn } from '@/utils/cn'

import { CopyTextButton } from './CopyTextButton'

const MIN_TOOLTIP_LENGTH = 13
const DEFAULT_MODAL_WIDTH = 600
const DEFAULT_MODAL_HEIGHT = 400

export const ScriptModalCell = <TData extends MRT_RowData>({
	cell,
	column,
	isDisabled = false,
	isEditBtnDisabled = false,
	isModalContentDisabled: isModalContentDisabledProp,
	row,
	updateCellValue,
	customValue,
}: ScriptModalCellProps<TData>) => {
	const { t } = useAppContext()
	const [isModalOpen, setModalOpen] = useState(false)
	const [aceValue, setAceValue] = useState<string>('')

	const isModalContentDisabled = isModalContentDisabledProp ?? isDisabled

	const cellValue = cell?.getValue()

	const onSubmit = () => {
		setModalOpen(false)
		updateCellValue?.(
			row?.index as number,
			column?.id as keyof TData & string,
			aceValue as TData[keyof TData],
		)
	}

	const handleCloseModal = () => {
		setModalOpen(false)
		setAceValue(getInitCellValue(customValue, cellValue))
	}

	const handleOpenModal = useCallback(() => {
		if (!isEditBtnDisabled) {
			setModalOpen(true)
		}
	}, [isEditBtnDisabled])

	useSelectRowOnOpen(isModalOpen, row?.original)

	const elementLabel = getRowElementLabel(row?.original)

	useEffect(() => {
		setAceValue(getInitCellValue(customValue, cellValue))
	}, [cellValue, customValue])

	const inputClassName = cn('h-full flex-1 min-w-0 px-2', {
		'text-black': isDisabled,
	})

	const showTooltip = (aceValue?.length || 0) > MIN_TOOLTIP_LENGTH

	const codePreview = useMemo(
		() =>
			aceValue ? <MultilinePreview value={aceValue} showLineNumbers /> : null,
		[aceValue],
	)

	return (
		<>
			<CellTooltip
				title={showTooltip ? aceValue : undefined}
				isMenuOpen={isModalOpen}
				customContent={showTooltip ? codePreview : undefined}
				isEditMode={!isModalContentDisabled}
			>
				<div className="flex items-center w-full">
					<input
						className={cn(inputClassName, 'border-none w-full bg-transparent')}
						type="text"
						value={aceValue}
						disabled={isDisabled}
						readOnly
					/>
					<Tooltip title={t('EDIT_IN_MODAL')}>
						<span>
							<IconButton
								onClick={handleOpenModal}
								disabled={isEditBtnDisabled}
								size="small"
								aria-label={t('EDIT_IN_MODAL')}
								data-testid="modal-button-edit"
							>
								<EditIcon />
							</IconButton>
						</span>
					</Tooltip>
				</div>
			</CellTooltip>

			<ModalForm
				open={isModalOpen}
				onClose={handleCloseModal}
				onSubmit={onSubmit}
				header={
					<EditorModalHeader
						attributeLabel={column?.header}
						elementLabel={elementLabel}
					/>
				}
				contentStyle={{
					width: DEFAULT_MODAL_WIDTH,
					height: DEFAULT_MODAL_HEIGHT,
				}}
				resizable
				maximizeButtonVisible
				hasSubmitButton={!isModalContentDisabled}
				cancelTitle={isModalContentDisabled ? t('CLOSE') : t('CANCEL')}
				footerBefore={<CopyTextButton text={aceValue} />}
			>
				<AutoSizer>
					{({ width, height }: { height: number; width: number }) => (
						<AceEditorWithoutForm
							name="scriptModal"
							value={aceValue}
							onChange={(value) => {
								setAceValue(value as string)
							}}
							onKeyPress={({ key, metaKey }) => {
								if (metaKey && key === 'Enter' && !isModalContentDisabled) {
									onSubmit()
								}
							}}
							autoFocus
							height={height}
							width={`${width}px`}
							disabled={isModalContentDisabled}
							isInModal
						/>
					)}
				</AutoSizer>
			</ModalForm>
		</>
	)
}
